import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue'
  import { UserModule } from '@/store/modules/user'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import useIcons from '@/composables/useIcons'
  import OverviewActionBar from '@/components/user-management/OverviewActionBar.vue'
  import UserTable from '@/components/user-management/UserTable.vue'
  import TextRenderer from '@/components/editor/TextRenderer.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import { CourseModule } from '@/store/modules/course'
  import useCommonMixin from '@/composables/useCommonMixin'
  import eventBus from '@/main'
  import useI18n from '@/composables/useI18n'
  import type { MemberGroup } from '@/services/interfaces/Auth0'
  import UserGroupEditor from '@/components/user-management/UserGroupEditor.vue'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'GroupManagementTabView',
  emits: ['reload-user-members'],
  setup(__props) {

  

  const { plusVectorIcon, pencilIcon } = useIcons()
  const { groupBackgroundColor, userMembersPerPageLimit } = useCommonMixin()
  const { translateString } = useI18n()

  const editingKey = ref('')
  const loadingMoreUsers = ref(false)

  const userGroups = computed(() => UserModule.userGroups || [])

  const hasNewGroup = computed(() => userGroups.value.some((g) => g.id === ''))

  const allUserGroupMembers = computed(() => UserModule.userMembers?.groups)

  const totalGroups = computed(() => UserModule.userGroups?.length ?? 0)

  const specificGroupMembersResponse = computed(() => (groupId: string) => {
    return allUserGroupMembers.value?.find((g) => g.groupId === groupId)
  })

  const specificGroupTotalUsersCount = computed(() => (groupId: string) => {
    if (!specificGroupMembersResponse.value(groupId)) return 0
    return specificGroupMembersResponse.value(groupId)?.totalUsers ?? 0
  })

  const specificGroupUsersCount = computed(() => (groupId: string) => {
    if (!specificGroupMembersResponse.value(groupId)) return 0
    return specificGroupMembersResponse.value(groupId)?.users.length ?? 0
  })

  const loadedAllGroupUsers = computed(() => (groupId: string) => {
    return specificGroupTotalUsersCount.value(groupId) === specificGroupUsersCount.value(groupId)
  })

  const xMoreUsersToLoad = computed(() => (groupId: string) => {
    return specificGroupTotalUsersCount.value(groupId) - specificGroupUsersCount.value(groupId) >
      userMembersPerPageLimit.value
      ? userMembersPerPageLimit.value
      : specificGroupTotalUsersCount.value(groupId) - specificGroupUsersCount.value(groupId)
  })

  const loadXMore = computed(
    () => (groupId: string) => translateString('loadXMore', { number: xMoreUsersToLoad.value(groupId) }),
  )

  const groupUsers = computed(() => (groupId: string) => specificGroupMembersResponse.value(groupId)?.users ?? [])

  const totalCourses = computed(() => (group: MemberGroup) => CourseModule.groupAssignedCourses(group.id).length)

  const onCancel = (key: string) => {
    if (key === '') {
      const index = UserModule.userGroups?.findIndex((g) => g.id === key)
      if (index && index > -1) {
        UserModule.userGroups?.splice(index, 1)
      }
      return
    } else {
      editingKey.value = ''
    }
  }

  const groupColor = computed(() => {
    return (index: number) => {
      const colors = UserModule.groupColors
      if (!colors) return { id: '' }
      return colors[index % colors.length]
    }
  })

  const container = ref<any>(null)
  const addNewGroup = () => {
    if (!UserModule.userGroups?.some((g) => g.id === '')) {
      UserModule.userGroups?.push({
        id: '',
        name: '',
        description: '',
        includeInGroupProfileQuestion: false,
        colorId: groupColor.value(UserModule.userGroups.length).id,
      })
      setTimeout(() => {
        const el = container.value?.querySelector('.group-management-container__group.new') as HTMLDivElement
        if (el) {
          window.scroll({
            top: el.offsetTop,
            behavior: 'smooth',
          })
          container.value?.querySelector('.group-management-container__group.new .title input')?.focus()
        }
      }, 1)
    }
  }

  const showModal = (group: MemberGroup) => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'AssignGroupCourseModal',
      modalProps: {
        group,
      },
      cssClass: 'assign-group-course-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const openAssignCoursesToGroupModal = (group: MemberGroup) => {
    if (!CourseModule.groupCourses) {
      eventBus.$emit('turn-on-loader')

      const interval = setInterval(() => {
        if (CourseModule.groupCourses) {
          clearInterval(interval)
          eventBus.$emit('turn-off-loader')
          showModal(group)
        }
      }, 100)
    } else {
      showModal(group)
    }
  }

  const loadMoreGroupUsers = (groupId: string) => {
    if (specificGroupMembersResponse.value(groupId)) {
      loadingMoreUsers.value = true
      UserModule.loadMoreUsers({
        perPage: userMembersPerPageLimit.value,
        page: specificGroupMembersResponse.value(groupId)!.currentPage + 1,
        groups: [groupId],
        userCategory: 'groups',
      }).finally(() => (loadingMoreUsers.value = false))
    }
  }

  const loadAllGroupUsers = (groupId: string) => {
    if (specificGroupMembersResponse.value(groupId)) {
      loadingMoreUsers.value = true
      UserModule.loadMoreUsers({
        perPage: userMembersPerPageLimit.value,
        page: specificGroupMembersResponse.value(groupId)!.currentPage + 1,
        groups: [groupId],
        userCategory: 'groups',
        loadAll: true,
      }).finally(() => (loadingMoreUsers.value = false))
    }
  }

  onMounted(() => {
    if (!CourseModule.groupCourses) {
      CourseModule.getGroupCourses()
    }
  })

return { __sfc: true,plusVectorIcon, pencilIcon, groupBackgroundColor, userMembersPerPageLimit, translateString, editingKey, loadingMoreUsers, userGroups, hasNewGroup, allUserGroupMembers, totalGroups, specificGroupMembersResponse, specificGroupTotalUsersCount, specificGroupUsersCount, loadedAllGroupUsers, xMoreUsersToLoad, loadXMore, groupUsers, totalCourses, onCancel, groupColor, container, addNewGroup, showModal, openAssignCoursesToGroupModal, loadMoreGroupUsers, loadAllGroupUsers, computed, onMounted, ref, UserModule, KetchUpButton, SVGRenderer, useIcons, OverviewActionBar, UserTable, TextRenderer, LoadingSpinner, CourseModule, useCommonMixin, eventBus, useI18n, UserGroupEditor }
}

})